import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { KeyRound, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const POLICY_HINT = 'At least 8 characters with one uppercase letter, one lowercase letter and one number.';

export function ChangePasswordDialog({ trigger }) {
  const { changePassword } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });

  const reset = () => { setForm({ current: '', next: '', confirm: '' }); setError(''); };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.next !== form.confirm) { setError('New passwords do not match'); return; }
    if (form.next.length < 8) { setError('New password must be at least 8 characters'); return; }
    setLoading(true);
    const res = await changePassword(form.current, form.next);
    setLoading(false);
    if (res.success) {
      toast.success('Password changed successfully');
      reset();
      setOpen(false);
    } else {
      setError(res.error || 'Could not change password');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" title="Change password" data-testid="change-password-trigger">
            <KeyRound className="h-5 w-5" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>{POLICY_HINT}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cp-current">Current password</Label>
            <Input id="cp-current" type="password" required autoComplete="current-password"
              value={form.current} onChange={(e) => setForm((p) => ({ ...p, current: e.target.value }))}
              data-testid="cp-current-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-new">New password</Label>
            <Input id="cp-new" type="password" required minLength={8} autoComplete="new-password"
              value={form.next} onChange={(e) => setForm((p) => ({ ...p, next: e.target.value }))}
              data-testid="cp-new-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input id="cp-confirm" type="password" required minLength={8} autoComplete="new-password"
              value={form.confirm} onChange={(e) => setForm((p) => ({ ...p, confirm: e.target.value }))}
              data-testid="cp-confirm-input" />
          </div>
          {error && (
            <div className="flex items-start gap-2 text-sm text-red-500" data-testid="cp-error">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="cp-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ChangePasswordDialog;
